import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../../services/ApiService';
import companionService, { CompanionHealth } from '../../services/CompanionService';
import { useToast } from '../../contexts/ToastContext';

interface ApprovedAnalysis {
    id: number;
    status: string;
    summary?: string;
    provider: string;
    model: string;
    reviewedAt?: string;
}

interface PatchFile {
    filePath: string;
    unifiedDiff: string;
    changeSummary: string;
    risk: string;
}

interface PatchDraft {
    id: number;
    issueId: number;
    analysisId: number;
    status: 'Pending' | 'Ready' | 'Failed' | 'Approved' | 'Rejected';
    provider: string;
    agent: string;
    summary?: string;
    canGeneratePatch: boolean;
    files: PatchFile[];
    validationCommands: string[];
    assumptions: string[];
    blockingReasons: string[];
    errorMessage?: string;
    reviewedByEmail?: string;
    reviewNote?: string;
    createdAt: string;
    reviewedAt?: string;
}

interface PatchRun {
    draftId: number;
    issueId: number;
    analysisId: number;
    agent: string;
    prompt: string;
    healthPort: number;
}

interface CompanionInfo {
    healthPort: number;
    version: string;
    available: boolean;
}

const STATUS_BADGE: Record<string, string> = {
    Pending: 'bg-warning text-dark',
    Ready: 'bg-primary',
    Failed: 'bg-danger',
    Approved: 'bg-success',
    Rejected: 'bg-secondary',
};

const IssuePatchDraftPanel: React.FC<{ issueId: number }> = ({ issueId }) => {
    const { showToast } = useToast();
    const [analyses, setAnalyses] = useState<ApprovedAnalysis[]>([]);
    const [drafts, setDrafts] = useState<PatchDraft[]>([]);
    const [selectedAnalysisId, setSelectedAnalysisId] = useState<number | null>(null);
    const [agent, setAgent] = useState('codex');
    const [workspace, setWorkspace] = useState(() => localStorage.getItem('easytools.patchDraft.workspace') || '');
    const [health, setHealth] = useState<CompanionHealth | null>(null);
    const [healthPort, setHealthPort] = useState(4977);
    const [companionError, setCompanionError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [checking, setChecking] = useState(false);
    const [busy, setBusy] = useState(false);
    const [reviewNotes, setReviewNotes] = useState<Record<number, string>>({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [analysisRows, draftRows] = await Promise.all([
                apiService.adminGetDiagnosticIssueAnalyses<ApprovedAnalysis>(issueId),
                apiService.adminGetDiagnosticIssuePatchDrafts<PatchDraft>(issueId),
            ]);
            const approved = analysisRows.filter(item => item.status === 'Approved');
            setAnalyses(approved);
            setDrafts(draftRows);
            setSelectedAnalysisId(current =>
                current != null && approved.some(item => item.id === current) ? current : approved[0]?.id ?? null);
        } catch (error) {
            showToast('Patch Lab', error instanceof Error ? error.message : 'Dati Patch Lab non disponibili', 'danger');
        } finally {
            setLoading(false);
        }
    }, [issueId, showToast]);

    useEffect(() => { void load(); }, [load]);

    const checkCompanion = useCallback(async (): Promise<{ info: CompanionInfo; health: CompanionHealth }> => {
        setChecking(true);
        setCompanionError(null);
        try {
            const info = await apiService.getCompanionInfo<CompanionInfo>();
            setHealthPort(info.healthPort);
            const currentHealth = await companionService.health(info.healthPort);
            if (currentHealth.version !== info.version)
                throw new Error(`Companion ${currentHealth.version} non compatibile: installare la versione ${info.version}.`);
            setHealth(currentHealth);
            const firstUsable = currentHealth.agents.find(item => item.available && item.supportsReadOnly);
            if (firstUsable && !currentHealth.agents.some(item => item.code === agent && item.available && item.supportsReadOnly))
                setAgent(firstUsable.code);
            return { info, health: currentHealth };
        } catch (error) {
            const message = error instanceof Error ? error.message : 'EasyToolsCompanion non raggiungibile.';
            setHealth(null);
            setCompanionError(message);
            throw error;
        } finally {
            setChecking(false);
        }
    }, [agent]);

    useEffect(() => { void checkCompanion().catch(() => undefined); }, [checkCompanion]);

    const usableAgents = useMemo(
        () => health?.agents.filter(item => item.available && item.supportsReadOnly) ?? [],
        [health],
    );

    const generate = async () => {
        if (selectedAnalysisId == null) return;
        setBusy(true);
        try {
            const { info, health: currentHealth } = await checkCompanion();
            const selectedAgent = currentHealth.agents.find(item => item.code === agent);
            if (!selectedAgent?.available || !selectedAgent.supportsReadOnly)
                throw new Error('La CLI selezionata non è disponibile in modalità read-only.');

            localStorage.setItem('easytools.patchDraft.workspace', workspace.trim());
            const run = await apiService.adminPrepareDiagnosticIssuePatchDraft<PatchRun>(
                issueId, selectedAnalysisId, agent,
            );
            let output: string;
            try {
                output = await companionService.runReadOnly(
                    run.healthPort || info.healthPort, run.agent, run.prompt, workspace,
                );
            } catch (cliError) {
                const message = cliError instanceof Error ? cliError.message : 'Generazione Patch Draft interrotta.';
                await apiService.adminCompleteDiagnosticIssuePatchDraft(
                    issueId, run.draftId, undefined, message,
                ).catch(() => undefined);
                throw cliError;
            }

            const result = await apiService.adminCompleteDiagnosticIssuePatchDraft<PatchDraft>(
                issueId, run.draftId, output,
            );
            if (result.status === 'Failed') throw new Error(result.errorMessage || 'Patch Draft non valida.');
            showToast('Patch Lab', result.canGeneratePatch
                ? 'Bozza di patch pronta per la seconda review.'
                : 'Analisi completata: la CLI ha segnalato dei blocchi.', 'success');
        } catch (error) {
            showToast('Patch Lab', error instanceof Error ? error.message : 'Patch Draft non generata', 'danger');
        } finally {
            await load();
            setBusy(false);
        }
    };

    const review = async (draft: PatchDraft, decision: 'Approved' | 'Rejected') => {
        const note = reviewNotes[draft.id]?.trim();
        if (decision === 'Rejected' && !note) {
            showToast('Review Patch Draft', 'Inserisci una motivazione prima del rifiuto.', 'warning');
            return;
        }
        setBusy(true);
        try {
            await apiService.adminReviewDiagnosticIssuePatchDraft(issueId, draft.id, decision, note);
            showToast('Review Patch Draft', decision === 'Approved'
                ? 'Diff approvato. Non è stato applicato al workspace.'
                : 'Diff rifiutato.', 'success');
            await load();
        } catch (error) {
            showToast('Review Patch Draft', error instanceof Error ? error.message : 'Review non salvata', 'danger');
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="card border-warning-subtle mb-3">
            <div className="card-header bg-warning-subtle d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                    <strong><i className="bi bi-file-earmark-diff me-2" />Patch Lab</strong>
                    <div className="small text-body-secondary">Genera unified diff da una diagnosi approvata, senza modificare il repository.</div>
                </div>
                <span className="badge text-bg-light border">Seconda approvazione obbligatoria</span>
            </div>
            <div className="card-body">
                {loading && <div className="text-body-secondary"><span className="spinner-border spinner-border-sm me-2" />Caricamento…</div>}
                {!loading && analyses.length === 0 && (
                    <div className="alert alert-info mb-0">
                        Approva prima una proposta dell’Agent Analyst per abilitare la generazione della patch.
                    </div>
                )}
                {!loading && analyses.length > 0 && (
                    <>
                        <div className="row g-3 align-items-end">
                            <div className="col-12 col-lg-4">
                                <label className="form-label small">Diagnosi approvata</label>
                                <select className="form-select" value={selectedAnalysisId ?? ''} disabled={busy}
                                    onChange={event => setSelectedAnalysisId(Number(event.target.value))}>
                                    {analyses.map(item => (
                                        <option key={item.id} value={item.id}>
                                            #{item.id} · {item.summary?.slice(0, 70) || `${item.provider} ${item.model}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-12 col-md-3 col-lg-2">
                                <label className="form-label small">CLI read-only</label>
                                <select className="form-select" value={agent} disabled={busy || usableAgents.length === 0}
                                    onChange={event => setAgent(event.target.value)}>
                                    {usableAgents.length === 0 && <option value={agent}>Nessuna CLI</option>}
                                    {usableAgents.map(item => <option key={item.code} value={item.code}>{item.displayName}</option>)}
                                </select>
                            </div>
                            <div className="col-12 col-md">
                                <label className="form-label small">Workspace locale</label>
                                <input className="form-control" value={workspace} disabled={busy}
                                    onChange={event => setWorkspace(event.target.value)}
                                    placeholder="C:\\Work\\EasyTools\\EasyToolsUI" />
                                <div className="form-text">Obbligatorio per verificare i file reali; non viene inviato al backend.</div>
                            </div>
                            <div className="col-12 col-md-auto d-flex gap-2">
                                <button className="btn btn-outline-secondary" disabled={busy || checking}
                                    onClick={() => void checkCompanion().catch(() => undefined)}>
                                    {checking ? 'Verifica…' : 'Verifica Companion'}
                                </button>
                                <button className="btn btn-warning" disabled={busy || !workspace.trim() || usableAgents.length === 0}
                                    onClick={() => void generate()}>
                                    {busy ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-file-earmark-diff me-2" />}
                                    Genera Patch Draft
                                </button>
                            </div>
                        </div>
                        <div className={`alert py-2 mt-3 mb-0 ${companionError ? 'alert-warning' : health ? 'alert-success' : 'alert-secondary'}`}>
                            {companionError
                                ? <><i className="bi bi-exclamation-triangle me-2" />{companionError} Porta: {healthPort}.</>
                                : health
                                    ? <><i className="bi bi-shield-check me-2" />Companion {health.version} pronto in sola lettura.</>
                                    : 'Verifica del Companion in corso…'}
                        </div>
                    </>
                )}
            </div>

            {drafts.length > 0 && (
                <div className="card-footer bg-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>Bozze generate ({drafts.length})</strong>
                        <button className="btn btn-sm btn-link" disabled={loading} onClick={() => void load()}>
                            <i className="bi bi-arrow-clockwise me-1" />Aggiorna
                        </button>
                    </div>
                    <div className="d-flex flex-column gap-3">
                        {drafts.map(draft => (
                            <article key={draft.id} className="border rounded p-3">
                                <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                                    <span className={`badge ${STATUS_BADGE[draft.status] || 'bg-secondary'}`}>{draft.status}</span>
                                    <span className="badge text-bg-light border">{draft.agent}</span>
                                    <span className="small text-body-secondary">Analisi #{draft.analysisId}</span>
                                    <span className="small text-body-secondary ms-auto">
                                        Patch #{draft.id} · {new Date(draft.createdAt).toLocaleString('it-IT')}
                                    </span>
                                </div>
                                {draft.status === 'Pending' && <div className="small text-body-secondary"><span className="spinner-border spinner-border-sm me-2" />Generazione in corso…</div>}
                                {draft.errorMessage && <div className="alert alert-danger py-2 mb-0">{draft.errorMessage}</div>}
                                {draft.summary && <p className="mb-2">{draft.summary}</p>}
                                {!draft.canGeneratePatch && draft.blockingReasons.length > 0 && (
                                    <div className="alert alert-warning py-2">
                                        <strong>Patch non generabile:</strong>
                                        <ul className="mb-0 mt-1">{draft.blockingReasons.map((item, index) => <li key={index}>{item}</li>)}</ul>
                                    </div>
                                )}
                                {draft.files.map(file => (
                                    <div key={file.filePath} className="border rounded mb-3 overflow-hidden">
                                        <div className="bg-body-tertiary px-2 py-1 d-flex flex-wrap gap-2 align-items-center">
                                            <code>{file.filePath}</code>
                                            <span className="small text-body-secondary">{file.changeSummary}</span>
                                            <span className="badge text-bg-light border ms-auto">rischio {file.risk}</span>
                                        </div>
                                        <pre className="small p-2 mb-0 overflow-auto" style={{ maxHeight: 420, whiteSpace: 'pre' }}>
                                            <code>{file.unifiedDiff}</code>
                                        </pre>
                                    </div>
                                ))}
                                {draft.validationCommands.length > 0 && (
                                    <details className="mb-2">
                                        <summary className="small fw-semibold">Comandi di validazione suggeriti</summary>
                                        <ul className="small mt-2 mb-0">{draft.validationCommands.map((item, index) => <li key={index}><code>{item}</code></li>)}</ul>
                                    </details>
                                )}
                                {draft.assumptions.length > 0 && (
                                    <details>
                                        <summary className="small fw-semibold">Assunzioni</summary>
                                        <ul className="small mt-2 mb-0">{draft.assumptions.map((item, index) => <li key={index}>{item}</li>)}</ul>
                                    </details>
                                )}
                                {draft.status === 'Ready' && draft.canGeneratePatch && (
                                    <div className="border-top mt-3 pt-3">
                                        <label className="form-label small">Nota seconda review</label>
                                        <textarea className="form-control mb-2" rows={2} disabled={busy}
                                            value={reviewNotes[draft.id] || ''}
                                            onChange={event => setReviewNotes(current => ({ ...current, [draft.id]: event.target.value }))}
                                            placeholder="Obbligatoria per il rifiuto" />
                                        <div className="d-flex gap-2 justify-content-end">
                                            <button className="btn btn-outline-danger" disabled={busy}
                                                onClick={() => void review(draft, 'Rejected')}>Rifiuta diff</button>
                                            <button className="btn btn-success" disabled={busy}
                                                onClick={() => void review(draft, 'Approved')}>
                                                <i className="bi bi-check2-circle me-1" />Approva diff
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {(draft.status === 'Approved' || draft.status === 'Rejected') && (
                                    <div className="small text-body-secondary border-top mt-3 pt-2">
                                        Review di {draft.reviewedByEmail || 'admin'}
                                        {draft.reviewedAt && ` · ${new Date(draft.reviewedAt).toLocaleString('it-IT')}`}
                                        {draft.reviewNote && <div className="mt-1">{draft.reviewNote}</div>}
                                        {draft.status === 'Approved' && <div className="mt-1 fw-semibold">Il diff non è stato applicato al repository.</div>}
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};

export default IssuePatchDraftPanel;
